// "What's included" — renders the current product's native bundle components
// (with quantity) by reading them from the Storefront GraphQL API. Fixed-bundle
// components aren't exposed in Liquid, so this is the supported way to list them.

const QUERY = `
  query Components($handle: String!) {
    product(handle: $handle) {
      variants(first: 20) {
        nodes {
          id
          components(first: 50) {
            nodes {
              quantity
              productVariant {
                id
                title
                price { amount currencyCode }
                image { url altText }
                product { title onlineStoreUrl featuredImage { url altText } }
              }
            }
          }
        }
      }
    }
  }
`;

class WhatsIncluded extends HTMLElement {
  connectedCallback() {
    if (this.dataset.hydrated) return;
    this.dataset.hydrated = 'true';
    this.render();
  }

  get config() {
    return {
      handle: this.dataset.handle,
      token: this.dataset.token,
      apiVersion: this.dataset.apiVersion || '2025-01',
      variantId: this.dataset.variantId,
      showQuantity: this.dataset.showQuantity === 'true',
      showWorth: this.dataset.showWorth === 'true',
    };
  }

  async render() {
    const { handle, token, apiVersion } = this.config;
    const list = this.querySelector('[data-wi-list]');
    if (!handle || !token || !list) return;

    let variants;
    try {
      // Same-origin request — no CORS. The Storefront token is public by design.
      // The Storefront API is always at the domain root, not under a locale path.
      const res = await fetch(`/api/${apiVersion}/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': token,
        },
        body: JSON.stringify({ query: QUERY, variables: { handle } }),
      });
      const json = await res.json();
      variants = json?.data?.product?.variants?.nodes;
    } catch (e) {
      variants = null;
    }

    if (!variants || variants.length === 0) {
      this.remove();
      return;
    }

    const components = this.componentsForVariant(variants);
    if (!components || components.length === 0) {
      this.remove();
      return;
    }

    list.innerHTML = components.map((c) => this.itemHTML(c)).join('');
    this.removeAttribute('hidden');
  }

  // Pick the components for the currently-selected variant, falling back to the
  // first variant that actually has components.
  componentsForVariant(variants) {
    const { variantId } = this.config;
    const byId =
      variantId && variants.find((v) => String(v.id).endsWith(`/${variantId}`) || v.id === variantId);
    const chosen = (byId && byId.components?.nodes?.length && byId) ||
      variants.find((v) => v.components?.nodes?.length) ||
      variants[0];
    return chosen?.components?.nodes || [];
  }

  itemHTML({ quantity, productVariant }) {
    const { showQuantity, showWorth } = this.config;
    const product = productVariant?.product || {};
    const title = product.title || productVariant?.title || '';
    const url = product.onlineStoreUrl;
    const img = productVariant?.image?.url || product.featuredImage?.url;
    const alt = productVariant?.image?.altText || product.featuredImage?.altText || title;

    const media = img
      ? `<div class="wi__media">
           <img class="wi__img" src="${this.resize(img, 120)}" alt="${this.esc(alt)}" loading="lazy" width="56">
           ${showQuantity && quantity > 1 ? `<span class="wi__qty-badge" aria-hidden="true">×${quantity}</span>` : ''}
         </div>`
      : '';

    const titleEl = url
      ? `<a class="wi__title" href="${this.esc(url)}">${this.esc(title)}</a>`
      : `<span class="wi__title">${this.esc(title)}</span>`;

    // "Worth £X" — the component's own price × its quantity in the bundle.
    const worth =
      showWorth && productVariant?.price
        ? `<span class="wi__worth">Worth ${this.money(productVariant.price, quantity)}</span>`
        : '';

    return `<li class="wi__item">${media}<div class="wi__info">${titleEl}${worth}</div></li>`;
  }

  // Ask Shopify's CDN for a smaller image.
  resize(url, width) {
    try {
      const u = new URL(url);
      u.searchParams.set('width', width);
      return u.toString();
    } catch {
      return url;
    }
  }

  money({ amount, currencyCode }, quantity = 1) {
    const value = parseFloat(amount) * quantity;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: currencyCode,
      }).format(value);
    } catch {
      return `${value.toFixed(2)} ${currencyCode}`;
    }
  }

  esc(str) {
    return String(str).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  }
}

if (!customElements.get('whats-included')) {
  customElements.define('whats-included', WhatsIncluded);
}
